import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusCircle, Trash2, Search } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { usePmProperty } from '@/hooks/usePmProperty';
import { getAnnouncements, createAnnouncement, deleteAnnouncement } from '@/services/api.client';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ConfirmDialog } from '@/components/pm/ConfirmDialog';
import type { Announcement, AnnouncementUrgency } from '@/types';
import { URGENCY_META } from '@/types';
import { PaginationControls } from '@/components/ui/pagination-controls';

const URGENCY_LEVELS: AnnouncementUrgency[] = ['normal', 'info', 'urgent', 'emergency'];

const schema = z.object({
  title: z.string().min(1, 'Title is required').max(120),
  body: z.string().min(1, 'Body is required'),
  urgency: z.enum(['normal', 'info', 'urgent', 'emergency'] as const),
  zoneId: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

function UrgencyChip({ urgency }: { urgency: AnnouncementUrgency }) {
  const meta = URGENCY_META[urgency];
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
      style={{ color: meta.textColor, border: `1px solid ${meta.color}` }}
    >
      {meta.label}
    </span>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-4 py-3">
      <p className="text-2xl font-semibold text-[var(--text-primary)]">{value}</p>
      <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">{label}</p>
    </div>
  );
}

export function AnnouncementsPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const qc = useQueryClient();
  const [panelOpen, setPanelOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [annPage, setAnnPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null);
  const ITEMS_PER_PAGE = 20;

  const query = useQuery({
    queryKey: ['pm', 'announcements', propertyId],
    queryFn: () => getAnnouncements(propertyId),
    enabled: !!propertyId,
  });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) =>
      createAnnouncement(propertyId, {
        title: values.title,
        body: values.body,
        urgency: values.urgency,
        ...(values.zoneId !== undefined && { zoneId: values.zoneId }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'announcements', propertyId] });
      setPanelOpen(false);
      reset();
      toast.success('Announcement sent successfully');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAnnouncement(propertyId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pm', 'announcements', propertyId] }),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { urgency: 'normal' },
  });
  const selectedUrgency = watch('urgency');

  const announcements = useMemo(() => query.data ?? [], [query.data]);

  const filteredAnnouncements = useMemo(() => {
    if (!searchTerm) return announcements;
    const lower = searchTerm.toLowerCase();
    return announcements.filter((a) => a.title.toLowerCase().includes(lower));
  }, [announcements, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredAnnouncements.length / ITEMS_PER_PAGE));
  const paginatedAnnouncements = useMemo(() => {
    const start = (annPage - 1) * ITEMS_PER_PAGE;
    return filteredAnnouncements.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAnnouncements, annPage]);

  const sent = announcements.length;
  const active = announcements.filter((a) => !a.archivedAt).length;
  const archived = announcements.filter((a) => !!a.archivedAt).length;
  const urgent = announcements.filter(
    (a) => a.urgency === 'urgent' || a.urgency === 'emergency',
  ).length;

  return (
    <div className="flex h-full gap-0">
      {/* Main column */}
      <div className="flex-1 space-y-5 p-6 overflow-auto">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl text-[var(--text-primary)]">Community Announcements</h1>
          <Button
            size="sm"
            onClick={() => setPanelOpen(true)}
            className="gap-1.5"
            style={{ background: 'var(--cta-primary-bg)', color: 'var(--text-inverse)' }}
          >
            <PlusCircle className="h-4 w-4" />
            Create Announcement
          </Button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Sent" value={sent} />
          <StatCard label="Active" value={active} />
          <StatCard label="Archived" value={archived} />
          <StatCard label="Urgent / Emergency" value={urgent} />
        </div>

        {/* Table */}
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search announcements..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setAnnPage(1);
            }}
            className="pl-9"
          />
        </div>
        <div className="rounded-xl border border-[var(--border-subtle)] overflow-hidden">
          {query.isError ? (
            <div
              role="alert"
              className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
            >
              <p className="text-sm font-medium">Couldn&apos;t load announcements</p>
              <p className="text-sm text-muted-foreground">Please try again later.</p>
            </div>
          ) : query.isLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : announcements.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-[var(--text-tertiary)]">No announcements yet.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface-raised)]">
                  {['Title', 'Urgency', 'Published', 'Status', ''].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedAnnouncements.map((ann) => (
                  <tr
                    key={ann.id}
                    className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-surface-raised)] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-[var(--text-primary)]">{ann.title}</p>
                      <p className="text-xs text-[var(--text-tertiary)] line-clamp-1">{ann.body}</p>
                    </td>
                    <td className="px-4 py-3">
                      <UrgencyChip urgency={ann.urgency} />
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {new Date(ann.publishedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {ann.archivedAt ? (
                        <span className="text-xs text-[var(--text-tertiary)]" aria-label="Archived">
                          –
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--status-green-text)' }}>
                          Live
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={deleteMutation.isPending}
                        onClick={() => setDeleteTarget(ann)}
                        aria-label="Delete announcement"
                      >
                        <Trash2 className="h-4 w-4" style={{ color: 'var(--status-red-text)' }} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <PaginationControls
          currentPage={annPage}
          totalPages={totalPages}
          onPageChange={setAnnPage}
        />
      </div>

      {/* Right slide-in panel as Sheet */}
      <Sheet
        open={panelOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPanelOpen(false);
            reset();
          }
        }}
      >
        <SheetContent className="w-80 sm:w-80 flex flex-col gap-4">
          <SheetHeader>
            <SheetTitle className="text-sm font-semibold text-[var(--text-primary)]">
              New Announcement
            </SheetTitle>
          </SheetHeader>

          <form
            onSubmit={handleSubmit((values) => createMutation.mutate(values))}
            className="flex flex-col gap-4 flex-1"
          >
            <div className="space-y-1.5">
              <Label htmlFor="ann-title" className="text-xs text-[var(--text-secondary)]">
                Title
              </Label>
              <Input
                id="ann-title"
                {...register('title')}
                className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)]"
              />
              {errors.title && (
                <p className="text-xs" style={{ color: 'var(--status-red-text)' }}>
                  {errors.title.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ann-body" className="text-xs text-[var(--text-secondary)]">
                Message
              </Label>
              <Textarea
                id="ann-body"
                rows={4}
                {...register('body')}
                className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)]"
              />
              {errors.body && (
                <p className="text-xs" style={{ color: 'var(--status-red-text)' }}>
                  {errors.body.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[var(--text-secondary)]">Urgency</Label>
              <div className="flex gap-1.5 flex-wrap">
                {URGENCY_LEVELS.map((lvl) => {
                  const meta = URGENCY_META[lvl];
                  const isSelected = selectedUrgency === lvl;
                  return (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setValue('urgency', lvl)}
                      aria-label={meta.label}
                      className="rounded px-2.5 py-1 text-xs font-medium transition-colors"
                      style={{
                        background: isSelected ? meta.color : 'transparent',
                        color: isSelected ? 'var(--bg-surface)' : meta.textColor,
                        border: `1px solid ${meta.color}`,
                      }}
                    >
                      {meta.label.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>

            {createMutation.isError && (
              <p className="text-xs" style={{ color: 'var(--status-red-text)' }}>
                Failed to create. Please try again.
              </p>
            )}

            <div className="mt-auto">
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="w-full rounded-lg py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{
                  background: 'var(--cta-primary-bg)',
                  color: 'var(--text-inverse)',
                  boxShadow: '0 4px 14px rgba(232,168,124,0.4)',
                }}
              >
                {createMutation.isPending ? 'Sending…' : 'Send Now'}
              </button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Announcement"
        description={`Are you sure you want to delete "${deleteTarget?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
